import { useEffect, useRef, useState } from "react";
import type { FormEvent } from "react";
import { Link, useLocation, useNavigate } from "react-router-dom";
import { titles } from "@/mocks/titles";
import { useFavorites } from "@/hooks/useFavorites";

const NAV_ITEMS = [
  { label: "首页", to: "/" },
  { label: "电影", to: "/category/movie" },
  { label: "电视剧", to: "/category/tv" },
  { label: "资讯", to: "/category/news" },
  { label: "关于我们", to: "/about" },
];

export default function SiteHeader() {
  const location = useLocation();
  const navigate = useNavigate();
  const [keyword, setKeyword] = useState("");
  const [mobileOpen, setMobileOpen] = useState(false);
  const [favoriteOpen, setFavoriteOpen] = useState(false);
  const [scrolled, setScrolled] = useState(false);
  const favoriteRef = useRef<HTMLDivElement | null>(null);
  const { favorites, clearFavorites } = useFavorites();

  useEffect(() => {
    setMobileOpen(false);
    setFavoriteOpen(false);
  }, [location.pathname, location.search]);

  useEffect(() => {
    const onScroll = () => setScrolled(window.scrollY > 8);
    onScroll();
    window.addEventListener("scroll", onScroll);
    return () => window.removeEventListener("scroll", onScroll);
  }, []);

  useEffect(() => {
    const onClick = (event: MouseEvent) => {
      if (favoriteRef.current && !favoriteRef.current.contains(event.target as Node)) {
        setFavoriteOpen(false);
      }
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const submitSearch = (event: FormEvent<HTMLFormElement>) => {
    event.preventDefault();
    const trimmed = keyword.trim();
    if (!trimmed) return;
    navigate(`/search?q=${encodeURIComponent(trimmed)}`);
    setMobileOpen(false);
  };

  const isActive = (to: string) =>
    to === "/" ? location.pathname === "/" : location.pathname.startsWith(to);

  const favoriteTitles = titles.filter((item) => favorites.includes(item.id));

  return (
    <header
      className={`sticky top-0 z-50 w-full border-b transition-colors ${
        scrolled
          ? "bg-background-50/97 backdrop-blur border-background-200"
          : "bg-background-50 border-background-100"
      }`}
    >
      <div className="w-full px-4 md:px-6 h-16 flex items-center justify-between gap-3 md:gap-6">
        <Link to="/" className="flex items-center gap-2.5 cursor-pointer shrink-0">
          <span className="w-9 h-9 rounded-md bg-primary-700 text-background-50 flex items-center justify-center font-heading font-extrabold text-lg">
            奇
          </span>
          <span className="flex flex-col leading-tight">
            <span className="font-heading font-extrabold text-[17px] text-foreground-950 tracking-tight">
              奇优影院
            </span>
            <span className="text-[10px] tracking-[0.18em] text-foreground-400 font-label uppercase">
              Qiyou Cinema
            </span>
          </span>
        </Link>

        <nav aria-label="主导航" className="hidden lg:flex items-center gap-1">
          {NAV_ITEMS.map((item) => (
            <Link
              key={item.to}
              to={item.to}
              className={`px-3.5 py-2 rounded-md text-sm transition-colors cursor-pointer whitespace-nowrap ${
                isActive(item.to)
                  ? "text-primary-700 bg-primary-50 font-medium"
                  : "text-foreground-600 hover:text-accent-600 hover:bg-background-100"
              }`}
            >
              {item.label}
            </Link>
          ))}
        </nav>

        <div className="flex items-center gap-2 md:gap-3">
          <form
            onSubmit={submitSearch}
            role="search"
            className="hidden md:flex items-center gap-1.5 h-9 px-3 rounded-md bg-background-100 border border-background-200 focus-within:border-primary-400 transition-colors"
          >
            <i className="ri-search-line text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              name="header-search"
              placeholder="搜索影片或剧集"
              aria-label="搜索影片或剧集"
              className="w-32 lg:w-40 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <button
              type="submit"
              className="text-xs text-primary-600 hover:text-accent-600 cursor-pointer whitespace-nowrap transition-colors"
            >
              搜索
            </button>
          </form>

          <div className="relative" ref={favoriteRef}>
            <button
              type="button"
              onClick={() => setFavoriteOpen((prev) => !prev)}
              aria-label="我的收藏"
              aria-expanded={favoriteOpen}
              className="relative w-9 h-9 rounded-md border border-background-200 text-foreground-600 hover:text-accent-600 hover:border-accent-300 transition-colors flex items-center justify-center cursor-pointer"
            >
              <i className="ri-heart-3-line text-base w-4 h-4 flex items-center justify-center" />
              {favorites.length > 0 && (
                <span className="absolute -top-1.5 -right-1.5 min-w-4 h-4 px-1 rounded-full bg-accent-500 text-background-50 text-[10px] leading-4 text-center font-label">
                  {favorites.length}
                </span>
              )}
            </button>
            {favoriteOpen && (
              <div className="absolute right-0 top-11 w-72 bg-background-50 border border-background-200 rounded-lg p-3 animate-slide-in">
                <div className="flex items-center justify-between mb-2">
                  <p className="text-sm font-semibold text-foreground-950">我的收藏</p>
                  {favorites.length > 0 && (
                    <button
                      type="button"
                      onClick={clearFavorites}
                      className="text-xs text-foreground-500 hover:text-accent-600 cursor-pointer transition-colors"
                    >
                      清空
                    </button>
                  )}
                </div>
                {favoriteTitles.length === 0 ? (
                  <p className="text-xs text-foreground-500 leading-relaxed py-3">
                    还没有收藏内容，进入任意影视详情页点击收藏即可添加（本机演示）。
                  </p>
                ) : (
                  <ul className="max-h-72 overflow-y-auto hide-scrollbar divide-y divide-background-100">
                    {favoriteTitles.map((item) => (
                      <li key={item.id}>
                        <Link
                          to={`/detail/${item.id}`}
                          className="flex items-center gap-2.5 py-2 group cursor-pointer"
                        >
                          <img
                            src={item.poster}
                            alt={`${item.title}海报`}
                            className="w-9 h-12 rounded object-cover object-top bg-primary-900"
                          />
                          <span className="flex-1 min-w-0">
                            <span className="block text-sm text-foreground-900 truncate group-hover:text-accent-700 transition-colors">
                              {item.title}
                            </span>
                            <span className="block text-[11px] text-foreground-500">
                              {item.type === "movie" ? "电影" : "剧集"} · {item.year}
                            </span>
                          </span>
                          <i className="ri-arrow-right-s-line text-foreground-400 w-4 h-4 flex items-center justify-center" />
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
                <p className="mt-2 pt-2 border-t border-background-100 text-[11px] text-foreground-400">
                  收藏状态保存在本地浏览器，仅作演示。
                </p>
              </div>
            )}
          </div>

          <button
            type="button"
            onClick={() => setMobileOpen((prev) => !prev)}
            aria-label="展开导航菜单"
            aria-expanded={mobileOpen}
            className="lg:hidden w-9 h-9 rounded-md border border-background-200 text-foreground-700 flex items-center justify-center cursor-pointer"
          >
            <i
              className={`${mobileOpen ? "ri-close-line" : "ri-menu-line"} text-lg w-5 h-5 flex items-center justify-center`}
            />
          </button>
        </div>
      </div>

      {mobileOpen && (
        <div className="lg:hidden border-t border-background-200 bg-background-50 px-4 py-4 animate-slide-in">
          <form
            onSubmit={submitSearch}
            role="search"
            className="flex items-center gap-2 h-10 px-3 rounded-md bg-background-100 border border-background-200 mb-3"
          >
            <i className="ri-search-line text-foreground-400 text-sm w-4 h-4 flex items-center justify-center" />
            <input
              value={keyword}
              onChange={(event) => setKeyword(event.target.value)}
              type="search"
              name="mobile-search"
              placeholder="搜索影片或剧集"
              aria-label="搜索影片或剧集"
              className="flex-1 bg-transparent border-none outline-none text-sm text-foreground-900 placeholder:text-foreground-400"
            />
            <button type="submit" className="text-xs text-primary-600 cursor-pointer whitespace-nowrap">
              搜索
            </button>
          </form>
          <nav aria-label="移动端导航" className="flex flex-col">
            {NAV_ITEMS.map((item) => (
              <Link
                key={item.to}
                to={item.to}
                className={`px-2 py-3 rounded-md text-sm border-b border-background-100 last:border-0 cursor-pointer ${
                  isActive(item.to) ? "text-primary-700 font-medium" : "text-foreground-700"
                }`}
              >
                {item.label}
              </Link>
            ))}
          </nav>
        </div>
      )}
    </header>
  );
}